import React from "react";
import api from "../../lib/api";
import { Upload, Trash2, Image as ImageIcon, Loader2, Check } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminStudioRefs() {
  const [refs, setRefs] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState("");
  const [progress, setProgress] = React.useState({ current: 0, total: 0, currentName: "" });
  const [dragOver, setDragOver] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const load = () => api.get("/admin/studio-refs").then(r => setRefs(r.data));
  React.useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadError("");
    setUploading(true);
    const arr = Array.from(files);
    setProgress({ current: 0, total: arr.length, currentName: arr[0]?.name || "" });
    let errors = [];
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      setProgress({ current: i, total: arr.length, currentName: f.name });
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", "");
        await api.post("/admin/studio-refs/upload", fd, {
          timeout: 120000, // 2 min per file for big images
        });
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Erreur upload";
        errors.push(`${f.name} → ${msg}`);
      }
    }
    setProgress({ current: arr.length, total: arr.length, currentName: "" });
    setUploading(false);
    if (errors.length) setUploadError(errors.join(" · "));
    await load();
  };

  const onFileInput = (e) => uploadFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette photo de référence ?")) return;
    await api.delete(`/admin/studio-refs/${id}`);
    load();
  };

  const saveLabel = async (id, label) => {
    await api.patch(`/admin/studio-refs/${id}`, { label });
    setEditing(null);
    load();
  };

  return (
    <div data-testid="admin-studio-refs">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Vidéo · Sora 2</div>
          <h1 className="h-display text-4xl md:text-5xl">Photos de <span className="italic-accent">référence.</span></h1>
          <p className="text-sm text-text-cream/60 mt-3 max-w-2xl font-light">
            Uploade tes vraies photos de studio (JPEG haute résolution jusqu'à <strong className="text-accent-light">60 Mo</strong>).
            Elles serviront de référence visuelle pour générer les vidéos publicitaires : Sora reproduira <em>ton</em> studio, tes lumières, ton ambiance.
          </p>
        </div>
      </div>

      {/* Upload dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed p-10 mb-10 text-center transition ${
          dragOver ? "border-accent-deep bg-accent-deep/10" : "border-white/20 bg-white/5"
        }`}
        data-testid="studio-refs-dropzone"
      >
        <Upload size={32} className="mx-auto text-accent-light mb-3" strokeWidth={1.2}/>
        <p className="text-lg font-light text-text-cream mb-2">
          Glisse tes photos ici <span className="text-text-cream/40">ou</span>
        </p>
        <label className="btn-primary cursor-pointer inline-flex" data-testid="studio-refs-upload-btn">
          <ImageIcon size={14}/> Choisir des fichiers
          <input
            type="file"
            multiple
            accept="image/*,.heic,.heif"
            onChange={onFileInput}
            disabled={uploading}
            className="hidden"
            data-testid="studio-refs-file-input"
          />
        </label>
        <p className="text-xs text-text-cream/40 mt-4 font-light">
          JPG, PNG, WEBP, HEIC · jusqu'à 60 Mo par photo · sélection multiple OK
        </p>

        {uploading && (
          <div className="mt-6 max-w-md mx-auto" data-testid="studio-refs-upload-progress">
            <div className="flex items-center justify-center gap-2 text-sm text-accent-light mb-2">
              <Loader2 size={14} className="animate-spin"/>
              Upload {progress.current + 1} / {progress.total} — {progress.currentName}
            </div>
            <div className="w-full h-1 bg-white/10">
              <div
                className="h-full bg-accent-deep transition-all"
                style={{ width: `${((progress.current) / Math.max(progress.total, 1)) * 100}%` }}
              />
            </div>
          </div>
        )}

        {uploadError && (
          <div className="text-xs text-red-400 mt-4" data-testid="studio-refs-upload-error">
            {uploadError}
          </div>
        )}
      </div>

      {/* Grid */}
      {refs.length === 0 ? (
        <div className="text-center text-text-cream/40 py-16 font-light" data-testid="studio-refs-empty">
          Aucune photo de référence pour l'instant.<br/>Upload tes photos pour commencer.
        </div>
      ) : (
        <>
          <div className="eyebrow text-text-cream/50 mb-4">{refs.length} référence{refs.length > 1 ? "s" : ""}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {refs.map(r => (
              <div key={r.id} className="group relative bg-white/5 border border-white/10 overflow-hidden" data-testid={`studio-ref-${r.id}`}>
                <div className="aspect-[4/5] overflow-hidden bg-dark">
                  <img
                    src={`${BASE}${r.thumb_url}`}
                    alt={r.label || r.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3">
                  {editing === r.id ? (
                    <input
                      type="text"
                      defaultValue={r.label}
                      autoFocus
                      onBlur={(e) => saveLabel(r.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveLabel(r.id, e.target.value);
                        if (e.key === "Escape") setEditing(null);
                      }}
                      className="input-editorial text-xs w-full"
                      data-testid={`studio-ref-label-input-${r.id}`}
                    />
                  ) : (
                    <button
                      onClick={() => setEditing(r.id)}
                      className="text-xs text-text-cream/70 hover:text-accent-light w-full text-left truncate"
                      data-testid={`studio-ref-label-${r.id}`}
                      title={r.label || "Cliquer pour ajouter un label"}
                    >
                      {r.label || <span className="text-text-cream/30 italic">+ Ajouter un label</span>}
                    </button>
                  )}
                  <div className="text-[10px] text-text-cream/40 mt-1 flex justify-between">
                    <span>{r.width}×{r.height}</span>
                    <span>{(r.size_bytes / (1024*1024)).toFixed(1)} Mo</span>
                  </div>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition flex gap-1">
                  <a
                    href={`${BASE}${r.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 bg-black/70 border border-white/20 hover:border-accent-deep"
                    title="Voir en pleine résolution"
                    data-testid={`studio-ref-view-${r.id}`}
                  >
                    <ImageIcon size={12}/>
                  </a>
                  <button
                    onClick={() => remove(r.id)}
                    className="p-1.5 bg-black/70 border border-white/20 hover:border-red-400"
                    title="Supprimer"
                    data-testid={`studio-ref-delete-${r.id}`}
                  >
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
